import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

import {
  businessPartnerDrawerSections,
  businessPartnerFormInitialValues
} from '@common/constants';
import {
  useCreateBusinessPartner,
  useGetActivePersonsWithoutBusinessPartners,
  useGetBusinessPartnerTypeEnums,
  useGetCountries,
  useGetCurrencies,
  useGetPersonDetails,
  useGetRelatedCompanies,
  useGetTitles,
  useGetZones
} from '@common/slices';
import DynamicFormDrawer from '@components/dynamic-form/DynamicFormDrawer';
import { IDynamicFormWidgetSection } from '@components/dynamic-form/types';
import ToastErrorMessage from '@components/toast/ToastErrorMessage';
import { IAbstractRecord } from '@models';
import { toLookups } from '@modules/salesforce/business-partners/business-partners-listing/salesforce-business-partners-drawer/utils';
import { DEFAULT_ERROR_TEXT } from '@utils';
import { getError } from '@utils/graph-utils';

interface Props {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  businessPartnerId?: string;
  salesForceBusinessPartnerDetailsInfo?: IAbstractRecord;
}

const BusinessPartnerDrawer: React.FC<Props> = ({
  open,
  onClose,
  salesForceBusinessPartnerDetailsInfo
}) => {
  const [lovs, setLovs] = useState<Record<string, Record<string, string>>>({});
  const [formSections, setFormSections] = useState<IDynamicFormWidgetSection[]>(
    []
  );
  const [values, setValues] = useState<IAbstractRecord>(
    salesForceBusinessPartnerDetailsInfo || businessPartnerFormInitialValues
  );
  const [booted, setBooted] = useState(false);
  const navigate = useNavigate();

  const [fetchPersonDetails] = useGetPersonDetails.useLazy();
  const { data: personsWithoutBusinessPartners } =
    useGetActivePersonsWithoutBusinessPartners.useQuery();
  const { data: companies } = useGetRelatedCompanies.useQuery();
  const { data: titles } = useGetTitles.useQuery();
  const { data: countries } = useGetCountries.useQuery();
  const { data: zones } = useGetZones.useQuery();
  const { data: currencies } = useGetCurrencies.useQuery();
  const { data: businessPartnerTypes } =
    useGetBusinessPartnerTypeEnums.useQuery();
  const [createBusinessPartner] = useCreateBusinessPartner();

  const valuesRef = useRef(values);
  const allDataLoaded =
    personsWithoutBusinessPartners?.salesforce
      ?.getActivePersonsWithoutBusinessPartners &&
    companies?.salesforce?.getRelatedCompanies &&
    titles?.core?.getTitles &&
    countries?.core?.getCountries &&
    zones?.core?.getZones &&
    currencies?.core?.getCurrencies &&
    businessPartnerTypes?.BusinessPartnerType;
  const loadLookups = useCallback(async () => {
    try {
      const lov = toLookups({
        salesforce: {
          getActivePersonsWithoutBusinessPartners:
            personsWithoutBusinessPartners?.salesforce
              ?.getActivePersonsWithoutBusinessPartners,
          getRelatedCompanies: companies?.salesforce?.getRelatedCompanies
        },
        core: {
          getTitles: titles?.core?.getTitles,
          getCountries: countries?.core?.getCountries,
          getZones: zones?.core?.getZones,
          getCurrencies: currencies?.core?.getCurrencies
        },
        BusinessPartnerType: businessPartnerTypes?.BusinessPartnerType
      });

      setLovs(lov);
      setFormSections(businessPartnerDrawerSections(valuesRef.current, lov));
    } catch (err) {
      toast.error(
        <ToastErrorMessage>Failed to load dropdowns</ToastErrorMessage>
      );
    }
  }, [allDataLoaded, valuesRef]);

  const handleChange = async (
    fieldName: string,
    value: any,
    sectionIndex: number,
    currentValues: IAbstractRecord,
    errors: Record<string, string>,
    touched: Record<string, boolean>
  ) => {
    let updated = { ...currentValues, [fieldName]: value };

    const isHardSwitch = fieldName === 'type' || fieldName === 'newPerson';

    if (fieldName === 'newPerson') {
      updated = {
        ...businessPartnerFormInitialValues,
        newPerson: value
      };
    }

    if (fieldName === 'type') {
      updated = {
        ...businessPartnerFormInitialValues,
        newPerson: currentValues.newPerson,
        type: value
      };
    }

    if (fieldName === 'person' && value) {
      const { data } = await fetchPersonDetails({ variables: { id: value } });
      const p = data?.salesforce?.getPersonDetails;
      if (p) {
        updated.email = p.email || '';
        updated.mobileNumber = p.phoneDetails?.phoneNumber || '';
      }
    }

    const nextSections = businessPartnerDrawerSections(updated, lovs);
    setFormSections(nextSections);

    if (isHardSwitch) {
      Object.keys(errors).forEach((k) => delete errors[k]);
      Object.keys(touched).forEach((k) => delete touched[k]);
    }

    setValues(updated);
  };

  useEffect(() => {
    if (open && allDataLoaded) {
      loadLookups().then(() => setBooted(true));
    }
  }, [open, allDataLoaded, loadLookups]);

  return (
    <DynamicFormDrawer
      title={'New Business Partner'}
      isOpen={open}
      onClose={onClose}
      sections={formSections}
      loading={!booted}
      disableForm={false}
      onSubmit={async (formData) => {
        try {
          const payload = {
            newPerson: formData.newPerson,
            personType: formData.type || null,
            personTitle: formData.title || null,
            firstName: formData.firstName || null,
            middleName: formData.middleName || null,
            lastName: formData.lastName || null,
            dateOfBirth: formData.dateOfBirth || null,
            personId: formData.person || null,
            companyName: formData.companyName || null,
            email: formData.email || null,
            mobileNumber: formData.mobileNumber || null,
            relatedCompany: formData.relatedCompany || null,
            businessPartnerType: formData.bpType || null,
            isCompany: formData.isCompany,
            isLocal: formData.isLocal,
            isRegistered: formData.isRegistered,
            registrationNumber: formData.registerNumber || null,
            registrationExpiryDate: formData.registrationExpiryDate || null,
            addressType: formData.addressType || null,
            taxAuxiliary: formData.taxAuxiliary || null,
            auxiliary: formData.auxiliary || null,
            maxCreditLimitAllowedCurrency:
              formData.maxCreditLimitAllowedCurrency || null,
            maxCreditLimit: Number(formData.maxCreditLimitAllowed || 0),
            allowAccessToAllInsured: formData.allowAccessToAllInsured,
            address: {
              country: formData.country || null,
              zone: formData.zone || null,
              city: formData.city || null,
              street: formData.street || null,
              building: formData.building || null,
              addressDetails: formData.addressDetails || null,
              addressDetailsArabic: formData.addressDetailsArabic || null
            }
          };

          const res = await createBusinessPartner({
            createBusinessPartnerInput: payload
          });

          if (!res.errors?.length) {
            const createdId = res.data?.salesforce?.createBusinessPartner;
            toast.success('Business partner created successfully!');
            onClose();
            navigate(`/salesforce/business-partners/${createdId}`);
          } else {
            toast.error(<ToastErrorMessage>{getError(res)}</ToastErrorMessage>);
          }
        } catch (err) {
          toast.error(
            <ToastErrorMessage>{DEFAULT_ERROR_TEXT}</ToastErrorMessage>
          );
        }
      }}
      onChange={handleChange}
    />
  );
};

export { BusinessPartnerDrawer };
